import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Sidebar from "../components/ClientOnboardingWizard/Sidebar";
import DocumentViewerModal from "../components/DocumentViewerModal";
import { fetchClientDocuments, fetchClientDocumentUrl } from "../api/clientDocumentAPI";
import { useToast } from "../context/ToastContext";

function ClientDocumentList() {
  const { toast } = useToast();
  const { household_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [householdData, setHouseholdData] = useState(location.state?.householdData || null);
  const [clientToken, setClientToken] = useState(location.state?.clientToken || null);
  
  // Sidebar state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const stored = localStorage.getItem(`wizard_sidebar_collapsed_${household_id}`);
    return stored === 'true';
  });

  // Viewer state
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [viewerUrl, setViewerUrl] = useState(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [viewingDocId, setViewingDocId] = useState(null);

  // Save sidebar state to localStorage when it changes
  useEffect(() => {
    localStorage.setItem(`wizard_sidebar_collapsed_${household_id}`, sidebarCollapsed.toString());
  }, [sidebarCollapsed, household_id]);

  useEffect(() => {
    // Try to get clientToken from localStorage if not in state
    if (!clientToken) {
      const storedToken = localStorage.getItem(`client_token_${household_id}`);
      if (storedToken) {
        setClientToken(storedToken);
      }
    }
  }, [household_id, clientToken]);

  useEffect(() => {
    if (household_id && clientToken) {
      loadData();
    }
  }, [household_id, clientToken]);

  const loadData = async () => {
    setLoading(true);
    try {
      const docs = await fetchClientDocuments(household_id, clientToken);
      setDocuments(docs || []);
    } catch (error) {
      console.error("Failed to load data:", error);
      toast.error("Failed to load documents. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleViewDocument = async (doc) => {
    console.log("handleViewDocument called with:", doc);
    setViewingDocId(doc.id);
    try {
      const url = await fetchClientDocumentUrl(household_id, doc.id, clientToken);
      console.log("Received presigned URL:", url);
      setViewerUrl(url);
      setSelectedDoc(doc);
      setIsViewerOpen(true);
    } catch (error) {
      console.error("Failed to open document:", error);
      toast.error("Failed to open document. Please try again.");
    } finally {
      setViewingDocId(null);
    }
  };

  const handleCloseViewer = () => {
    setIsViewerOpen(false);
    setViewerUrl(null);
    setSelectedDoc(null);
  };

  const handleBack = () => {
    const returnPath = location.state?.returnPath || `/household/${household_id}/full-review`;
    navigate(returnPath, {
      state: { householdData, clientToken }
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "-";
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const yyyy = date.getFullYear();
    const time = date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
    return `${mm}/${dd}/${yyyy}, ${time}`;
  };

  const getFileIcon = (fileType, fileName) => {
    const type = (fileType || fileName?.split('.').pop() || '').toLowerCase();
    
    // PDF
    if (type.includes('pdf')) {
      return (
        <div className="w-12 h-12 bg-gradient-to-br from-red-50 to-red-100 rounded-xl flex items-center justify-center shadow-sm border border-red-200">
           <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        </div>
      );
    }
    // Excel/Sheets
    if (['xlsx', 'xls', 'csv', 'sheet'].some(ext => type.includes(ext))) {
      return (
        <div className="w-12 h-12 bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl flex items-center justify-center shadow-sm border border-emerald-200">
          <svg className="w-6 h-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
      );
    }
    // Images
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'image'].some(ext => type.includes(ext))) {
       return (
        <div className="w-12 h-12 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl flex items-center justify-center shadow-sm border border-purple-200">
          <svg className="w-6 h-6 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
      );
    }
    // Word/Docs
    if (['doc', 'docx', 'word'].some(ext => type.includes(ext))) {
       return (
        <div className="w-12 h-12 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl flex items-center justify-center shadow-sm border border-blue-200">
          <svg className="w-6 h-6 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
      );
    }
    // Default
    return (
      <div className="w-12 h-12 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl flex items-center justify-center shadow-sm border border-gray-200">
        <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      </div>
    );
  };

  // Show loading if no client token
  if (!clientToken) {
    return (
      <div className="flex flex-col min-h-screen bg-gradient-to-br from-emerald-50/30 via-white to-blue-50/20">
        <main className="flex-1 p-8 flex items-center justify-center">
          <div className="text-center">
            <p className="text-gray-600">Loading authentication...</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar - Collapsible */}
      <div className={`relative bg-white border-r border-gray-200 transition-all duration-300 ${sidebarCollapsed ? 'w-0' : 'w-1/5'}`}>
        {!sidebarCollapsed && (
          <Sidebar 
            householdData={householdData} 
            onViewDocuments={() => {}} // Already on documents page
          />
        )}
        {/* Toggle Button */}
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className={`absolute top-4 ${sidebarCollapsed ? 'left-2' : '-right-3'} z-10 bg-white border border-gray-200 rounded-full p-2 shadow-md hover:shadow-lg transition-all`}
        >
          {sidebarCollapsed ? (
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          ) : (
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          )}
        </button>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen bg-gradient-to-br from-emerald-50/30 via-white to-blue-50/20">
        <main className="flex-1 p-8 bg-gradient-to-b from-transparent to-emerald-50/10 overflow-y-auto">
          <div className="max-w-7xl mx-auto w-full">
            
            {/* Header / Breadcrumb Area - Centered Layout */}
            <div className="mb-10 relative z-10">
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/5 via-transparent to-blue-500/5 rounded-3xl blur-3xl -z-10"></div>
              
              <div className="flex flex-col items-center gap-6">
                {/* Back Button - Absolute Left or Top */}
                <div className="w-full max-w-6xl flex justify-start">
                    <button
                        onClick={handleBack}
                        className="group flex items-center gap-2 px-4 py-2 rounded-xl text-gray-600 hover:text-emerald-700 hover:bg-emerald-50/50 transition-all duration-200 font-medium border border-transparent hover:border-emerald-100"
                    >
                        <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        </div>
                        <span>Back</span>
                    </button>
                </div>

                  {/* Header Card - Left Aligned Text, Centered Component matching Table width */}
                  <div className="relative bg-white/80 backdrop-blur-sm rounded-3xl p-8 border border-emerald-100/50 shadow-xl w-full max-w-6xl text-left">
                      <div className="flex flex-col items-start justify-start gap-2">
                          <h1 className="text-4xl font-extrabold bg-gradient-to-r from-gray-900 via-emerald-700 to-gray-900 bg-clip-text text-transparent mb-2 tracking-tight">
                            Documents
                          </h1>
                          <div className="flex items-center justify-start gap-3 text-lg">
                              <span className="font-bold text-gray-800">{householdData?.name || "Household"}</span>
                          </div>
                      </div>
                  </div>
              </div>
            </div>

            {/* Content */}
            {loading ? (
              <div className="card bg-white/50 backdrop-blur-sm border-white/60 shadow-lg p-12 max-w-5xl mx-auto">
                <div className="flex flex-col items-center justify-center">
                    <div className="relative w-16 h-16 mb-4">
                      <div className="absolute inset-0 border-4 border-emerald-100 rounded-full"></div>
                      <div className="absolute inset-0 border-4 border-transparent border-t-emerald-500 rounded-full animate-spin"></div>
                    </div>
                    <p className="text-gray-500 font-medium animate-pulse">Loading documents...</p>
                </div>
              </div>
            ) : documents.length === 0 ? (
              <div className="card bg-gradient-to-br from-white via-emerald-50/20 to-blue-50/20 border-emerald-200/50 shadow-xl p-16 text-center max-w-4xl mx-auto">
                <div className="mx-auto w-24 h-24 bg-gradient-to-br from-emerald-100 to-blue-100 rounded-3xl flex items-center justify-center mb-6 shadow-lg rotate-3 hover:rotate-6 transition-transform duration-300">
                  <svg className="w-12 h-12 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6.586a1 1 0 011.414.586l4.414 4.414a1 1 0 01.586 1.414V19a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">No documents found</h3>
                <p className="text-gray-600 max-w-md mx-auto text-lg leading-relaxed">
                  Documents uploaded for this household will appear here.
                </p>
              </div>
            ) : (
              <div className="card bg-white border-emerald-100/50 shadow-xl overflow-hidden backdrop-blur-sm max-w-6xl mx-auto">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-100">
                    <thead>
                      <tr className="bg-gray-50/50">
                        {/* Left align name for readability, Center others */}
                        <th scope="col" className="px-8 py-5 text-left text-xs font-bold text-gray-500 uppercase tracking-wider w-1/3">
                          Document Details
                        </th>
                        <th scope="col" className="px-6 py-5 text-center text-xs font-bold text-gray-500 uppercase tracking-wider">
                          Type
                        </th>
                        <th scope="col" className="px-6 py-5 text-center text-xs font-bold text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th scope="col" className="px-6 py-5 text-center text-xs font-bold text-gray-500 uppercase tracking-wider">
                          Uploaded On
                        </th>
                        <th scope="col" className="px-8 py-5 text-center text-xs font-bold text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50 bg-white">
                      {documents.map((doc) => (
                        <tr key={doc.id} className="hover:bg-emerald-50/30 transition-all duration-200 group">
                          <td className="px-8 py-5 whitespace-nowrap">
                            <div className="flex items-center">
                              {/* Enhanced Icon */}
                              <div className="flex-shrink-0 transition-transform duration-300 group-hover:scale-105">
                                 {getFileIcon(doc.file_type, doc.file_name)}
                              </div>
                              
                              <div className="ml-5">
                                <div className="text-base font-bold text-gray-900 group-hover:text-emerald-700 transition-colors">
                                  {doc.file_name}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-5 whitespace-nowrap text-center">
                             <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide border ${
                                (doc.file_type || '').toLowerCase().includes('pdf') ? 'bg-red-50 text-red-700 border-red-100' :
                                (doc.file_type || '').toLowerCase().includes('image') ? 'bg-purple-50 text-purple-700 border-purple-100' :
                                'bg-emerald-50 text-emerald-700 border-emerald-100'
                             }`}>
                                {doc.file_type || 'Unknown'}
                             </span>
                          </td>
                          <td className="px-6 py-5 whitespace-nowrap text-center">
                             <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide border ${
                                doc.status === 'completed' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' :
                                doc.status === 'processing' ? 'bg-blue-50 text-blue-700 border-blue-100' :
                                doc.status === 'failed' ? 'bg-red-50 text-red-700 border-red-100' :
                                doc.status === 'confirmed' ? 'bg-yellow-50 text-yellow-700 border-yellow-100' :
                                doc.status === 'initiated' ? 'bg-gray-50 text-gray-700 border-gray-100' :
                                'bg-gray-50 text-gray-700 border-gray-100'
                             }`}>
                                {doc.status === 'initiated' || doc.status === 'confirmed' ? 'Uploaded' : (doc.status || 'Unknown')}
                             </span>
                          </td>
                          <td className="px-6 py-5 whitespace-nowrap text-sm font-medium text-gray-500 group-hover:text-gray-700 transition-colors text-center">
                            {formatDate(doc.created_at || doc.updated_at)}
                          </td>
                          <td className="px-8 py-5 whitespace-nowrap text-center">
                             <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewDocument(doc);
                                }}
                                disabled={viewingDocId === doc.id}
                                className="inline-flex items-center justify-center px-5 py-2.5 border border-transparent text-sm font-bold rounded-xl text-emerald-700 bg-emerald-50 hover:bg-emerald-100 hover:scale-105 transition-all duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none gap-2 min-w-[110px]"
                              >
                                {viewingDocId === doc.id ? (
                                    <>
                                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        <span>Loading</span>
                                    </>
                                ) : (
                                    <>
                                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                      </svg>
                                      <span>View</span>
                                    </>
                                )}
                              </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </main>

        <DocumentViewerModal
          isOpen={isViewerOpen}
          onClose={handleCloseViewer}
          documentUrl={viewerUrl}
          documentName={selectedDoc?.file_name}
        />
      </div>
    </div>
  );
}

export default ClientDocumentList;
